import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Address } from '../../models/context';
import { RegExp } from 'src/app/core/config/regex';
import { CommonService } from '../../services/common.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CompanyService } from '../../services/company.service';
import { DomainService } from '../../services/domain.service';
import { Postal } from '../../models/domain';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss']
})
export class AddressComponent implements OnInit {
  @Input() ref: string;
  @Input() onLoad: boolean = false;
  @Input() refId: number;
  public submitted: boolean = false;
  private modified: boolean = false;
  public isLoading: boolean = true;
  public addressForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private companyService: CompanyService,
    private domainService: DomainService,
    private settingsService:SettingsService
  ) { }

  ngOnInit(): void {
    if (this.refId && !this.onLoad) {
      this.getAddress(this.refId, this.ref);
    }
    this.setAddress();
  }

  public getAddress(refId: number, ref: string, clear: boolean = true) {
    if (clear) {
      this.isLoading = true;
    }
    this.settingsService.getAddresses(refId, ref).subscribe({
      next: (address: Address[]) => {
        this.setAddress(address[0]);
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  private setAddress(address?: Address) {
    if (!address) address = {
      createdDate: null,
      modifiedDate: null,
      modifiedBy: null,
      createdBy: null,
      id: null,
      addressee: this.refId,
      line: null,
      city: null,
      district: null,
      state: null,
      country: null,
      code: null,
      phone: null,
      email: null,
      latitude: null,
      longitude: null,
      stakeholderType: this.ref,
      main: false,
      disabled: false,
    } as Address;
    this.addressForm = this.fb.group({
      id: this.fb.control(address?.id || null),
      addressee: this.fb.control(this.refId, [Validators.required]),
      line: this.fb.control(address.line, [Validators.required, Validators.pattern(RegExp.address)]),
      city: this.fb.control(address.city, [Validators.required, Validators.pattern(RegExp.text)]),
      district: this.fb.control(address.district, [Validators.pattern(RegExp.text)]),
      state: this.fb.control(address.state, [Validators.required, Validators.pattern(RegExp.text)]),
      email: this.fb.control(address.email, [Validators.required, Validators.pattern(RegExp.email)]),
      country: this.fb.control(address.country, [Validators.required, Validators.pattern(RegExp.text)]),
      code: this.fb.control(address.code, [Validators.required, Validators.pattern(RegExp.zip)]),
      phone: this.fb.control(address.phone, [Validators.required, Validators.pattern(RegExp.mobile)]),
      latitude: this.fb.control(address.latitude, [Validators.pattern(RegExp.latAndLog)]),
      longitude: this.fb.control(address.longitude, [Validators.pattern(RegExp.latAndLog)]),
      stakeholderType: this.fb.control(this.ref || 'COMPANY', []),
      main: this.fb.control(address.main, []),
      disabled: this.fb.control(address.disabled, []),
    });
    this.addressForm.valueChanges.subscribe(value => this.modified = true);
  }

  get a() {
    return this.addressForm.controls;
  }

  public saveAddress(inProgress?: any) {
    this.submitted = true;
    if (this.addressForm.valid) {
      const address: Address = this.addressForm.value;
      if (!this.modified) {
        this.commonService.setToastr(200, `Address ${address.line} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (address.id) {
        this.settingsService.updateAddress(address.id, address).subscribe({
          next: (response: Address) => {
            if (response) {
              inProgress.value = false;
              this.commonService.setToastr(200, `Address ${response.line} has been updated successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            // this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      } else {
        this.settingsService.createAddress(address).subscribe({
          next: (response: Address) => {
            if (response) {
              inProgress.value = false;
              this.setAddress(response);
              this.commonService.setToastr(200, `Address ${response.line} has been created successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            // this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }

  public getPostalData(postCode: string) {
    if (!postCode || !RegExp.zip.test(postCode)) return;
    this.domainService.getPostalData(postCode).subscribe({
      next: (postals: Postal[]) => {
        this.addressForm.patchValue({
          country: postals[0].PostOffice[0].Country,
          state: postals[0].PostOffice[0].State,
          district: postals[0].PostOffice[0].District,
          city: postals[0].PostOffice[0].Name,
        } as Address);
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }
}
